import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Lock, Clock, ArrowRight, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useShowMemberCounts } from "@/hooks/useCommunityConfig";
import { plural } from "@/lib/utils";

// Botão com os três estados de adesão: Pedir adesão · Pedido pendente (cancelar) · Entrar na comunidade
export function JoinStateButton({ c, onChange, size = "md", onDark = false, tone, full = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const my = c.my_membership;
  const bx = tone === "bordeaux";
  const cls = `${size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2.5 text-sm"}${full ? " w-full" : ""}`;
  const primaryStyle = bx ? { background: "#7A1230", color: "#fff" } : undefined;
  const run = async (fn) => { setBusy(true); try { await fn(); onChange?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); } finally { setBusy(false); } };
  if (my?.status === "active") return <Link to={`/comunidade/${c.id}`} data-testid={`join-state-enter-${c.id}`} style={primaryStyle} className={`${cls} rounded-xl ${bx ? "" : "bg-[#c8102e] text-white"} font-semibold inline-flex items-center justify-center gap-1`}>Entrar na comunidade <ArrowRight size={14} /></Link>;
  if (my?.status === "pending") return (
    <div className={`flex items-center gap-2 flex-wrap ${full ? "justify-between" : ""}`}>
      <span data-testid={`join-state-pending-${c.id}`} className={`${size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2.5 text-sm"} rounded-xl font-semibold inline-flex items-center gap-1 ${bx ? "" : "bg-amber-50 text-amber-800"}`} style={bx ? { background: "#F3E7C9", color: "#7A1230" } : undefined}><Clock size={13} /> Pedido pendente</span>
      <button disabled={busy} onClick={() => run(async () => { await api.post(`/communities/${c.id}/leave`); toast.success("Pedido cancelado"); })} data-testid={`join-state-cancel-${c.id}`} className={`text-xs underline disabled:opacity-50 ${onDark ? "text-white/80" : "text-zinc-600"}`}>Cancelar pedido</button>
    </div>
  );
  if (my?.status === "banned") return <span className={`${cls} rounded-xl bg-zinc-100 text-zinc-500 font-semibold`}>Sem acesso</span>;
  const approval = c.join_policy === "approval";
  return (
    <button disabled={busy} data-testid={`join-state-request-${c.id}`} style={primaryStyle} className={`${cls} rounded-xl font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-50 ${bx ? "" : onDark ? "bg-white text-black" : "bg-black text-white"}`}
      onClick={() => {
        if (!user) { nav(`/login?next=${encodeURIComponent(`/comunidade/${c.id}`)}`); return; }
        run(async () => { const { data } = await api.post(`/communities/${c.id}/join`); toast.success(data.status === "pending" ? "Pedido enviado — aguarda aprovação de uma moderadora" : "Bem-vinda à comunidade!"); });
      }}>
      {approval ? <><Lock size={13} /> Pedir adesão</> : "Entrar"}
    </button>
  );
}

// Cartão compacto da página inicial — uma linha: logótipo · título + «Comunidade privada» · seta/estado. Clicável conforme o estado real.
export function WomenHomeCard({ c, onChange, testid = "home-women-card" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!c) return null;
  const my = c.my_membership;
  const state = my?.status === "active" ? "enter" : my?.status === "pending" ? "pending" : my?.status === "banned" ? "banned" : "request";
  const dest = `/comunidade/${c.id}`;
  const onCard = async () => {
    if (state === "enter" || state === "banned") return nav(dest);
    if (state === "pending") return nav(dest);
    if (!user) return nav(`/login?next=${encodeURIComponent(dest)}`);
    setBusy(true);
    try { const { data } = await api.post(`/communities/${c.id}/join`); toast.success(data.status === "pending" ? "Pedido enviado — aguarda aprovação de uma moderadora" : "Bem-vinda à comunidade!"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); }
    finally { setBusy(false); }
  };
  const cancel = async (e) => {
    e.stopPropagation(); setBusy(true);
    try { await api.post(`/communities/${c.id}/leave`); toast.success("Pedido cancelado"); onChange?.(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Erro"); }
    finally { setBusy(false); }
  };
  const label = { enter: "Entrar na comunidade", pending: "Pedido pendente", request: "Pedir adesão", banned: "Sem acesso" }[state];
  return (
    <div role="link" tabIndex={0} aria-label={`${c.name} — ${label}`} onClick={onCard} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onCard())}
      data-testid={testid} data-state={state} aria-busy={busy}
      className="flex items-center gap-3 rounded-2xl border px-3 py-2.5 min-h-[72px] cursor-pointer card-hover" style={{ background: "#FBF6EE", borderColor: "#D9C089" }}>
      <div className="w-12 h-12 rounded-xl shrink-0 flex items-center justify-center overflow-hidden" style={{ background: "#F8F0E3" }} data-testid={`${testid}-logo`}>
        <img src={c.logo_url || "/brand/angolanas-logo.png"} alt={`Logótipo ${c.name}`} className="w-full h-full object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm leading-tight truncate" style={{ color: "#7A1230" }} data-testid={`${testid}-name`}>{c.name}</div>
        <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap mt-0.5">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#9A7A2E" }} data-testid={`${testid}-private`}><Lock size={11} /> Comunidade privada</span>
          {state === "pending" && <span className="text-[11px] font-semibold" style={{ color: "#7A1230" }} data-testid={`join-state-pending-${c.id}`}>· Pedido pendente</span>}
          {state === "request" && <span className="text-[11px] text-zinc-500" data-testid={`join-state-request-${c.id}`}>· Pedir adesão</span>}
        </div>
      </div>
      {state === "pending"
        ? <button type="button" disabled={busy} onClick={cancel} data-testid={`join-state-cancel-${c.id}`} className="shrink-0 text-[11px] underline text-zinc-600 disabled:opacity-50 py-2">Cancelar pedido</button>
        : <ArrowRight size={18} className="shrink-0" style={{ color: "#7A1230" }} aria-hidden="true" data-testid={state === "enter" ? `join-state-enter-${c.id}` : undefined} />}
    </div>
  );
}

// Cartão de destaque (Início e página Comunidades) — sem excertos de conteúdo privado
export function WomenCommunityCard({ c, onChange, testid = "women-community-card" }) {
  const showCounts = useShowMemberCounts();
  if (!c) return null;
  return (
    <div data-testid={testid} className="rounded-2xl p-4 relative overflow-hidden text-white bg-[#0d0d0d]">
      <div className="auk-ribbon absolute top-0 left-0 right-0 h-1" />
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#c8102e] flex items-center justify-center shrink-0 overflow-hidden font-display font-extrabold text-lg">{c.cover_url ? <img src={c.cover_url} alt="" className="w-full h-full object-cover" /> : "A"}</div>
        <div className="flex-1 min-w-0">
          <Link to={`/comunidade/${c.id}`} className="font-display font-bold text-base leading-tight hover:underline" data-testid={`${testid}-name`}>{c.name}</Link>
          <div className="flex items-center gap-2 flex-wrap mt-1 text-[10px] font-semibold">
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-white/15 text-[#ffd100]" data-testid={`${testid}-official`}><BadgeCheck size={10} /> Comunidade oficial</span>
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-white/15 text-white/90" data-testid={`${testid}-private`}><Lock size={10} /> Conteúdo reservado às participantes</span>
          </div>
          <p className="text-xs text-white/75 leading-relaxed mt-2" data-testid={`${testid}-desc`}>{c.description}</p>
          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            {showCounts ? <span className="text-[11px] text-white/60 inline-flex items-center gap-1"><Users size={11} /> {plural(c.members_count, "participante", "participantes")}</span> : <span />}
            <JoinStateButton c={c} onChange={onChange} size="sm" onDark />
          </div>
        </div>
      </div>
    </div>
  );
}
